import React, { useCallback, useMemo, useRef, useState } from 'react'
import { ScrollbackMarker } from '@components/Channel/components/ScrollbackMarker'
import { FullyReadObserver } from '@components/MessageTimeIineItem/items/FullyReadObserver'
import { MessageTimelineItem } from '@components/MessageTimeIineItem/TimelineItem'
import { useVisualViewportContext } from '@components/VisualViewportContext/VisualViewportContext'
import { MessageType, RiverTimelineEvent, TimelineEvent } from '@towns-protocol/sdk'
import { Box, Divider, Paragraph, SizeBox } from '@ui'
import { SECOND_MS } from 'data/constants'
import { useChannelType } from 'hooks/useChannelType'
import { useDevice } from 'hooks/useDevice'
import { isEqual, uniqBy } from 'lodash-es'
import { useExperimentsStore } from 'store/experimentsStore'
import { VList } from 'ui/components/VList2/VList'
import { notUndefined } from 'ui/utils/utils'
import { useChannelData } from 'use-towns-client'

import { DateDivider } from '../MessageTimeIineItem/items/DateDivider'
import { NewDivider } from '../MessageTimeIineItem/items/NewDivider'

import { useFocusItem, useScrollbackToFocusItem } from './hooks/useFocusItem'
import { usePersistedUnreadMarkers } from './hooks/usePersistedUnreadMarkers'
import { useScrollback } from './hooks/useScrollback'
import { useTimelineContext } from './hooks/useTimelineContext'
import {
    EncryptedMessageRenderEvent,
    getEventsByDate,
    InteractionRequestRenderEvent,
    isChannelMessage,
    isInteractionRequest,
    isMissingMessage,
    isRedactedChannelMessage,
    isTokenTransfer,
    MessageRenderEvent,
    MissingMessageRenderEvent,
    RedactedMessageRenderEvent,
    RenderEventType,
    TokenTransferRenderEvent,
} from './util/getEventsByDate'
import { MessageTimelineType } from './MessageTimelineContext'
import { ListItem } from './types'

type Props = {
    header?: React.JSX.Element
    align: 'top' | 'bottom'
    highlightId?: string
    collapsed?: boolean
    groupByUser?: boolean
    displayAsSimpleList?: boolean
}

const emptyTimeline: TimelineEvent[] = []

const unhandledEventKinds = [
    RiverTimelineEvent.MiniblockHeader,
    RiverTimelineEvent.Fulfillment,
    RiverTimelineEvent.KeySolicitation,
]

export const MessageTimeline = (props: Props) => {
    const { groupByUser = true, displayAsSimpleList: displaySimpleList = false } = props
    const timelineContext = useTimelineContext()
    const repliesMap = timelineContext?.messageRepliesMap
    const channelId = timelineContext?.channelId
    const isChannelEncrypted = timelineContext?.isChannelEncrypted
    const channelName = timelineContext?.channels.find((c) => c.id === channelId)?.label
    const userId = timelineContext?.userId
    const editingMessageId = timelineContext?.timelineActions.editingMessageId
    const { isTouch } = useDevice()
    const [isCollapsed, setCollapsed] = useState(props.collapsed ?? false)
    const onExpandClick = useCallback(() => {
        setCollapsed(false)
    }, [])
    const channelData = useChannelData()

    const stableEventsRef = useRef<TimelineEvent[]>([])

    const ref = useRef<HTMLDivElement>(null)

    const { onFirstMessageReached, scrollbackState } = useScrollback(channelId)

    const events = useMemo(() => {
        // remove unneeded events
        let filtered = (timelineContext?.events ?? [])
            // strip out unsolicited events (e.g. key solicitations)
            .filter((e) => e.content?.kind && !unhandledEventKinds.includes(e.content?.kind))

        // strip out fresh encrypted events. Optimistically we want to show
        // the decrypted content when ready. However, the bar will appear if
        // it takes to long or if newer messages are getting decrypted
        const lastDecryptedIndex = filtered.findLastIndex(
            (e) => e.content?.kind === RiverTimelineEvent.ChannelMessage,
        )
        filtered = filtered.filter(
            (e, index) =>
                e.content?.kind !== RiverTimelineEvent.ChannelMessageEncrypted ||
                index < lastDecryptedIndex ||
                e.createdAtEpochMs < Date.now() - SECOND_MS * 10,
        )
        // remove duplicates - NOTE: this shouldn't happen - but it does
        const result = uniqBy(filtered ?? emptyTimeline, (t) => t.eventId)

        if (isEqual(result, stableEventsRef.current)) {
            // no need to update to mutate and update the UI if the events are the same
            return result
        }

        stableEventsRef.current = result

        return stableEventsRef.current
    }, [timelineContext?.events])

    const isStartupRef = useRef(true)

    if (events.length > 0) {
        isStartupRef.current = false
    }

    const { fullyReadMarker, fullreadMarkerPersisted, onMarkAsRead, isUnreadMarkerFaded } =
        usePersistedUnreadMarkers({ channelId, threadId: timelineContext?.threadParentId })

    const [initialFullyReadMarker] = useState(() =>
        fullyReadMarker?.isUnread ? fullyReadMarker : undefined,
    )

    const experiments = useExperimentsStore()

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
    //

    const isThread = timelineContext?.type === MessageTimelineType.Thread
    const channelType = useChannelType(channelId)

    const _dateGroups = useMemo(
        () =>
            getEventsByDate(
                events,
                channelType,
                channelData,
                userId,
                fullreadMarkerPersisted?.eventId,
                isThread,
                repliesMap,
                experiments,
                groupByUser,
            ),
        [
            channelType,
            events,
            experiments,
            fullreadMarkerPersisted?.eventId,
            groupByUser,
            isThread,
            repliesMap,
            channelData,
            userId,
        ],
    )

    // prevent re-renders
    const groupRefs = useRef(_dateGroups)
    if (!isEqual(groupRefs.current, _dateGroups)) {
        groupRefs.current = _dateGroups
    }
    const dateGroups = groupRefs.current

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
    //                                    estimate height of blocks before they get rendered

    const estimateItemHeight = useCallback((r: ListItem) => {
        if (r.type === 'user-messages') {
            const height = r.item.events.reduce((height, item) => {
                const itemHeight =
                    !item.isRedacted &&
                    item.content.kind === RiverTimelineEvent.ChannelMessage &&
                    item.content.content.msgType === MessageType.Image
                        ? 400
                        : 60
                return height + itemHeight
            }, 0)
            return height
        }
        if (r.type === 'message') {
            const height = [r.item.event].reduce((height, item) => {
                const itemHeight =
                    isChannelMessage(item) && item.content.content.msgType === MessageType.Image
                        ? 400
                        : 60
                return height + itemHeight
            }, 0)
            return height
        }
        if (r.type === 'divider') {
            return 50
        }
        return 50
    }, [])

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
    //                                                               group and filter events

    const flatGroups = useMemo(
        () =>
            dateGroups.flatMap((f, groupIndex) => {
                const flattened = [
                    {
                        type: 'group',
                        date: f.date.humanDate,
                        isNew: f.isNew,
                        key: f.key,
                    } as const,
                    ...f.events,
                ]

                // positions the channel intro at the top before date groups
                if (groupIndex === 0) {
                    const createRoomEventIndex = flattened.findIndex(
                        (e) => e.type === RenderEventType.ChannelHeader,
                    )
                    if (createRoomEventIndex > -1) {
                        // puts the create event at top before the date group
                        flattened.unshift(flattened.splice(createRoomEventIndex, 1)[0])
                    }
                }

                return flattened
            }),
        [dateGroups],
    )

    const lastMessageOnInitIdRef = useRef<string | undefined>(undefined)

    const allListItems = useMemo(() => {
        const groupByDate = timelineContext?.type === MessageTimelineType.Channel

        const listItems: ListItem[] = flatGroups
            .flatMap<ListItem>((e) => {
                if (e.type === RenderEventType.UserMessages) {
                    // if all consecutive messages are encrypted, we can group them
                    const displayEncrypted = e.events.every(
                        (e) => e.content.kind === RiverTimelineEvent.ChannelMessageEncrypted,
                    )
                    // only picks 1 message (last) of an encrypted group
                    const filtered = displayEncrypted
                        ? e.events.filter((e, i, events) => i === 0)
                        : e.events

                    return filtered
                        .map((event, index, events) => {
                            const stableKey = event.localEventId ?? event.eventId
                            let item:
                                | null
                                | MessageRenderEvent
                                | EncryptedMessageRenderEvent
                                | RedactedMessageRenderEvent
                                | MissingMessageRenderEvent
                                | TokenTransferRenderEvent
                                | InteractionRequestRenderEvent

                            if (isChannelMessage(event)) {
                                item = {
                                    type: RenderEventType.Message,
                                    key: stableKey,
                                    event,
                                    displayContext: getMessageDisplayContext(index, events.length),
                                    attachments: event.content.attachments,
                                }
                            } else if (isRedactedChannelMessage(event)) {
                                if (repliesMap?.has(event.eventId)) {
                                    item = {
                                        type: RenderEventType.RedactedMessage,
                                        key: stableKey,
                                        event,
                                        displayContext: getMessageDisplayContext(
                                            index,
                                            events.length,
                                        ),
                                    }
                                } else {
                                    item = null
                                }
                            } else if (isTokenTransfer(event)) {
                                item = {
                                    type: RenderEventType.TokenTransfer,
                                    key: stableKey,
                                    event,
                                    displayContext: getMessageDisplayContext(index, events.length),
                                }
                            } else if (isMissingMessage(event)) {
                                item = {
                                    type: RenderEventType.MissingMessage,
                                    key: stableKey,
                                    event,
                                    displayContext: getMessageDisplayContext(index, events.length),
                                }
                            } else if (isInteractionRequest(event)) {
                                item = {
                                    type: RenderEventType.InteractionRequest,
                                    key: stableKey,
                                    event,
                                    displayContext: getMessageDisplayContext(index, events.length),
                                }
                            } else {
                                item = {
                                    type: RenderEventType.EncryptedMessage,
                                    key: stableKey,
                                    event,
                                    displayContext: getMessageDisplayContext(index, events.length),
                                }
                            }

                            return item
                                ? ({
                                      key: stableKey,
                                      type: 'message',
                                      item,
                                  } as const)
                                : undefined
                        })
                        .filter(notUndefined)
                }

                return e.type === 'group' && groupByDate
                    ? ({ key: e.key, type: 'group', date: e.date, isNew: e.isNew } as const)
                    : e.type === RenderEventType.NewDivider
                      ? ({ key: e.key, type: 'new-divider', item: e } as const)
                      : e.type === RenderEventType.ThreadUpdate
                        ? ({ key: e.key, type: 'thread-update', item: e } as const)
                        : e.type !== 'group'
                          ? ({ key: e.key, type: 'generic', item: e } as const)
                          : []
            })
            .filter(notUndefined)

        return listItems
    }, [flatGroups, repliesMap, timelineContext?.type])

    const { listItems, numHidden } = useMemo(() => {
        const isThreadOrigin =
            timelineContext?.type === MessageTimelineType.Thread && flatGroups.length > 1

        const listItems = [...allListItems]
        if (timelineContext?.type === MessageTimelineType.Channel) {
            listItems.unshift({ key: 'header', type: 'header' } as const)
        } else if (isThreadOrigin && listItems.length > 1 && listItems[1]?.type !== 'new-divider') {
            listItems.splice(1, 0, { key: 'divider', type: 'divider' } as const)
        }

        /*
         counts chunks of messages backwards, we want to show about the last 2
         for now a chunk is defined as a sequence of messages from the same
         user or an image
        */

        if (!lastMessageOnInitIdRef.current && allListItems.length > 0) {
            // keep track of the last message when opening the timeline, this marker
            // enables us to figure which messages are to be considered as new
            lastMessageOnInitIdRef.current = allListItems[allListItems.length - 1]?.key
        }
        const collapseStats = allListItems.reduceRight(
            (prev, curr, index) => {
                if (curr.type === 'message') {
                    if (groupByUser) {
                        const userId = curr.item.event.sender.id
                        const date = Math.floor(
                            curr.item.event.createdAtEpochMs / (1000 * 60 * 60 * 24),
                        )
                        if (
                            // count first chunk
                            typeof prev.collapseEndIndex === 'undefined' ||
                            // count if user changed
                            userId !== prev.lastUserId ||
                            date !== prev.lastDate ||
                            // count if message is an image
                            (!isRedactedChannelMessage(curr.item.event) &&
                                curr.item.event.content.kind ===
                                    RiverTimelineEvent.ChannelMessage &&
                                curr.item.event.content.content.msgType === MessageType.Image)
                        ) {
                            prev.chunkCount++
                            prev.lastUserId = userId
                            prev.lastDate = date
                        }
                    } else {
                        // anything after the last message is considered new
                        // and I guess we want to show it, once the the last
                        // message has been reached we count count 2 messages
                        // and collapse
                        if (curr.key === lastMessageOnInitIdRef.current) {
                            prev.isNewMessage = false
                        }

                        if (!prev.isNewMessage || !lastMessageOnInitIdRef.current) {
                            prev.chunkCount++
                        }
                    }
                }

                if (
                    prev.chunkCount <= 2 ||
                    (fullreadMarkerPersisted?.isUnread &&
                        curr.key === fullreadMarkerPersisted?.eventId)
                ) {
                    // index until which collapsed section ends
                    prev.collapseEndIndex = index - 1
                }
                return prev
            },
            {
                chunkCount: 0,
                collapseEndIndex: 1,
                lastUserId: undefined,
                lastDate: undefined,
                isNewMessage: true,
            } as {
                chunkCount: number
                collapseEndIndex: number
                lastUserId: string | undefined
                lastDate: number | undefined
                isNewMessage: boolean
            },
        )

        const numHidden = collapseStats.collapseEndIndex - 1 // not counting parent message
        // no need to collapse if there's only 1 hidden message
        if (isCollapsed && collapseStats.collapseEndIndex && numHidden > 1) {
            listItems.splice(1, collapseStats.collapseEndIndex, {
                key: 'expanded',
                type: 'expander',
            })
        }

        return { listItems, numHidden }
    }, [
        allListItems,
        flatGroups.length,
        fullreadMarkerPersisted?.eventId,
        fullreadMarkerPersisted?.isUnread,
        groupByUser,
        isCollapsed,
        timelineContext?.type,
    ])

    const groupIds = useMemo(
        () =>
            listItems.reduce((groupIds, item) => {
                if (item.type === 'group') {
                    groupIds.push(item.key)
                }
                return groupIds
            }, [] as string[]),
        [listItems],
    )

    useScrollbackToFocusItem(
        props.highlightId,
        timelineContext.events,
        scrollbackState,
        onFirstMessageReached,
    )

    const validHighlightId = listItems.find((e) => e.key === props.highlightId)?.key

    const { focusItem } = useFocusItem(listItems, validHighlightId, userId, initialFullyReadMarker)

    // Keep edited items mounted to preserve editor state when scrolled out of view
    const persistedIds = useMemo(
        () => (editingMessageId ? [editingMessageId] : undefined),
        [editingMessageId],
    )

    const { visualViewportScrolled: tabBarHidden } = useVisualViewportContext()

    const scrollbackMarker = useMemo(
        () => (
            <ScrollbackMarker
                watermark={
                    timelineContext && timelineContext.events.length > 0
                        ? scrollbackState?.fromInclusiveMiniblockNum || -1n
                        : undefined
                }
                containerRef={ref}
                onMarkerReached={onFirstMessageReached}
            />
        ),
        [onFirstMessageReached, scrollbackState?.fromInclusiveMiniblockNum, timelineContext],
    )
    const itemRenderer = useCallback(
        (r: ListItem, measureRef?: React.RefObject<HTMLDivElement> | undefined) => {
            switch (r.type) {
                case 'header': {
                    return <>{scrollbackMarker}</>
                }
                case 'group': {
                    return (
                        <DateDivider
                            label={r.date}
                            ref={measureRef}
                            new={r.isNew}
                            faded={isUnreadMarkerFaded}
                        />
                    )
                }
                case 'divider': {
                    return (
                        <Box paddingX="md" paddingY="md" display={isTouch ? 'none' : 'flex'}>
                            <Divider space="none" />
                        </Box>
                    )
                }
                case 'expander': {
                    return (
                        <Box paddingX="md" paddingY="md">
                            <Box cursor="pointer" onClick={onExpandClick}>
                                <Paragraph color="cta2">Show {numHidden} more messages</Paragraph>
                            </Box>
                        </Box>
                    )
                }
                case 'new-divider': {
                    return (
                        <NewDivider
                            faded={isUnreadMarkerFaded}
                            paddingX={
                                timelineContext?.type === MessageTimelineType.Channel ? 'lg' : 'md'
                            }
                        />
                    )
                }

                default: {
                    if (r.item.type === RenderEventType.ChannelHeader) {
                        return props.header ?? <></>
                    }

                    const message = (
                        <MessageTimelineItem
                            itemData={r.item}
                            highlight={r.key === props.highlightId}
                            userId={userId}
                            channelName={channelName}
                            channelType={channelType}
                            channelEncrypted={isChannelEncrypted}
                        />
                    )

                    return fullyReadMarker?.eventId === r.key ? (
                        <>
                            <FullyReadObserver
                                fullyReadMarker={fullyReadMarker}
                                onMarkAsRead={onMarkAsRead}
                            />
                            {message}
                        </>
                    ) : (
                        message
                    )
                }
            }
        },
        [
            channelName,
            channelType,
            fullyReadMarker,
            isChannelEncrypted,
            isTouch,
            isUnreadMarkerFaded,
            numHidden,
            onExpandClick,
            onMarkAsRead,
            props.header,
            props.highlightId,
            scrollbackMarker,
            timelineContext?.type,
            userId,
        ],
    )

    const offscreenMarker = useMemo(() => {
        return (
            <Box padding="lg">
                <Box
                    horizontal
                    centerContent
                    gap="sm"
                    height="height_lg"
                    paddingX="lg"
                    rounded="lg"
                    cursor="pointer"
                    background="accent"
                    boxShadow="medium"
                >
                    <Paragraph fontWeight="medium">↓</Paragraph>
                    <Paragraph fontWeight="medium">new messages</Paragraph>
                </Box>
            </Box>
        )
    }, [])

    return displaySimpleList ? (
        <Box paddingY="md">
            {listItems.map((item) => (
                <React.Fragment key={item.key}>{itemRenderer(item)}</React.Fragment>
            ))}
        </Box>
    ) : (
        <SizeBox grow position="relative" justifyContent="end">
            <VList
                overscan={1}
                align={props.align}
                list={listItems}
                padding={16}
                focusItem={focusItem}
                estimateHeight={estimateItemHeight}
                getItemKey={(item) => item.key}
                getItemFocusable={(item) =>
                    item.type === 'user-messages' ||
                    item.type === 'message' ||
                    item.type === 'header'
                }
                containerRef={ref}
                key={channelId}
                groupIds={groupIds}
                persistedIds={persistedIds}
                pointerEvents={isTouch && tabBarHidden ? 'none' : undefined}
                itemRenderer={itemRenderer}
                itemDataTestId="chat-message-container"
                offscreenMarker={offscreenMarker}
            />
        </SizeBox>
    )
}

const getMessageDisplayContext = (index: number, total: number) => {
    if (index === 0) {
        return total === 1 ? 'single' : 'head'
    }

    return index === total - 1 ? 'tail' : 'body'
}
